/**
 * @fileoverview Map Coordinator Hook
 *
 * Bu hook, harita arama işlemlerini koordine eder.
 * Map search, bounds ve empty state yönetimini birleştirir.
 *
 * @module hooks/useMapCoordinator
 */

import { useState, useCallback } from "react";
import { useMapSearch } from "./useMapSearch.js";
import { LIMITS } from "../utils";

/**
 * @typedef {Object} UseMapCoordinatorParams
 * @property {Function} setOutput - Output setter
 * @property {Function} setCallError - Hata setter
 * @property {Function} handleCloseDetail - Detay kapatma fonksiyonu
 */

/**
 * @typedef {Object} UseMapCoordinatorReturn
 * @property {boolean} isMapSearching - Harita araması devam ediyor mu
 * @property {boolean} mapSearchEmpty - Harita araması boş sonuç döndürdü mü
 * @property {Function} handleMapSearchInArea - Belirli alanda ara
 * @property {Function} resetMapSearchState - Harita arama state'ini sıfırla
 */

/**
 * Harita arama işlemlerini koordine eden hook.
 *
 * Bu hook, useMapSearch hook'unu sarmalayarak harita viewport
 * araması için yüksek seviyeli bir API sağlar.
 *
 * @function useMapCoordinator
 * @param {UseMapCoordinatorParams} params - Hook parametreleri
 * @returns {UseMapCoordinatorReturn} Harita arama state ve handler'ları
 *
 * @example
 * const {
 *   isMapSearching,
 *   mapSearchEmpty,
 *   handleMapSearchInArea,
 * } = useMapCoordinator({
 *   setOutput,
 *   setCallError,
 *   handleCloseDetail,
 * });
 *
 * // "Bu alanda ara" butonunda
 * <button onClick={() => handleMapSearchInArea(bounds, query)}>
 *   Bu alanda ara
 * </button>
 */
export function useMapCoordinator({
  setOutput,
  setCallError,
  handleCloseDetail,
}) {
  // Map search empty state
  const [mapSearchEmpty, setMapSearchEmpty] = useState(false);

  // Core map search hook
  const { isSearching: isMapSearching, searchInBounds } = useMapSearch({
    /**
     * Arama tamamlandığında çağrılır (sonuç var)
     */
    onSearchComplete: (result) => {
      setMapSearchEmpty(false);
      setOutput(result);
      // Açık detayı kapat - kullanıcı yeni alan arıyor
      handleCloseDetail?.();
    },
    /**
     * Boş sonuç geldiğinde çağrılır (sonuç yok)
     */
    onEmptyResults: () => {
      setMapSearchEmpty(true);
      // Açık detayı kapat
      handleCloseDetail?.();
    },
    /**
     * Hata durumunda çağrılır
     */
    onError: setCallError,
  });

  /**
   * Belirli harita bölgesinde arama yap handler'ı.
   *
   * @param {Object} bounds - Harita sınırları
   * @param {Object} query - Arama sorgusu
   */
  const handleMapSearchInArea = useCallback((bounds, query) => {
    if (!query?.path) return;
    searchInBounds({
      path: query.path,
      filters: query.filters,
      bounds,
      limit: LIMITS.MAP_SEARCH,
    });
  }, [searchInBounds]);

  /**
   * Harita arama state'ini sıfırla.
   * Yeni ChatGPT aramasında çağrılmalıdır.
   */
  const resetMapSearchState = useCallback(() => {
    setMapSearchEmpty(false);
  }, []);

  return {
    isMapSearching,
    mapSearchEmpty,
    handleMapSearchInArea,
    resetMapSearchState,
  };
}

export default useMapCoordinator;
